/**
 * Dashboard services translations: email, shop, packet
 */

export const email = {
  title: 'E-Mail',
  subtitle: 'Verwalte deine E-Mail-Accounts und Aliase',
  stats: {
    total: 'Gesamt',
    accounts: 'Accounts',
    aliases: 'Aliase',
    active: 'Aktiv',
    inactive: 'Inaktiv'
  },
  domainSelector: {
    label: 'Domain',
    all: 'Alle Domains',
    filtering: 'Gefiltert nach {domain}'
  },
  searchPlaceholder: 'Suchen und Enter drücken...',
  addEmail: 'E-Mail hinzufügen',
  table: {
    email: 'E-Mail',
    nameRecipient: 'Name / Empfänger',
    type: 'Typ',
    usage: 'Speicher',
    actions: 'Aktionen'
  },
  types: {
    account: 'Account',
    alias: 'Alias'
  },
  edit: 'Bearbeiten',
  delete: 'Löschen',
  viewDetails: 'Details anzeigen',
  loading: 'E-Mail-Accounts werden geladen...',
  empty: {
    title: 'Keine E-Mail-Accounts vorhanden',
    description: 'Erstelle einen neuen E-Mail-Account oder Alias um loszulegen.',
    action: 'E-Mail erstellen'
  },
  noResults: 'Keine E-Mails gefunden für die aktuelle Filterung',
  copiedToClipboard: 'Erfolgreich kopiert',
  copiedMessage: '\'{email}\' wurde erfolgreich in die Zwischenablage kopiert',
  slider: {
    titleNew: 'Neue E-Mail',
    titleEdit: 'E-Mail bearbeiten',
    subtitleCreate: 'erstellen',
    subtitleUpdate: 'aktualisieren',
    typeLabel: 'Typ',
    emailAddressLabel: 'E-Mail-Adresse',
    emailAddressRequired: 'E-Mail-Adresse *',
    addressReadOnlyHint: 'Die Adresse kann nach dem Anlegen nicht geändert werden – lösche den Eintrag und erstelle ihn neu, um eine andere Adresse zu verwenden.',
    namePlaceholder: 'benutzer',
    domainPlaceholder: 'domain.de',
    senderLabel: 'Sender Adressen',
    recipientLabel: 'Empfänger',
    passwordLabel: 'Passwort',
    passwordLabelRequired: 'Passwort *',
    passwordPlaceholder: 'Passwort eingeben',
    passwordConfirmPlaceholder: 'Passwort wiederholen',
    activeLabel: 'Aktiv',
    activeHint: 'E-Mail-Adresse ist aktiv und kann Nachrichten empfangen',
    cancel: 'Abbrechen',
    save: 'Speichern',
    create: 'Erstellen',
    required: 'Pflichtfeld',
    nameRules: 'Erlaubte Zeichen: a-z A-Z 0-9 - .',
    domainRules: 'Du kannst nur Domains verwenden, die du besitzt',
    emailFormat: 'Falsches E-Mail Format',
    passwordWeak: 'Passwort nicht ausreichend',
    passwordMismatch: 'Passwörter stimmen nicht überein',
    createSuccess: 'E-Mail wurde erstellt',
    updateSuccess: 'E-Mail wurde aktualisiert',
    saveError: 'E-Mail konnte nicht gespeichert werden'
  },
  deleteModal: {
    title: 'Account löschen',
    confirmMessage: 'Bist du sicher? Diese Aktion kann nicht rückgängig gemacht werden!',
    confirmLabel: 'Gib {email} ein',
    confirmPlaceholder: '{email}',
    cancel: 'Abbrechen',
    delete: 'Löschen',
    error: 'Löschen fehlgeschlagen'
  },
  senderRecipient: {
    senderTooltip: 'Zusätzliche Absenderadresse(n) für dieses Konto. Der Benutzer kann mit dieser Adresse E-Mails versenden, auch wenn sie lokal nicht vorhanden ist. Gib eine Adresse pro Eingabe an. Drücke ENTER, um eine neue Eingabe hinzuzufügen.',
    recipientTooltip: 'Adressen, auf die dieser Alias verweisen soll. Gib nur eine Adresse pro Eingabe an und drücke ENTER, um eine neue Eingabe hinzuzufügen.',
    emailFormat: 'Falsches Email-Format',
    domainMismatch: 'Domains müssen übereinstimmen'
  },
  password: {
    required: 'Pflichtfeld',
    weak: 'Passwort nicht ausreichend',
    mismatch: 'Passwörter stimmen nicht überein'
  },
  tabs: {
    mailboxes: 'Postfächer',
    domain: 'Domain-Einstellungen',
    subdomains: 'Subdomains',
    delegates: 'Delegierte',
    dns: 'DNS & Einrichtung'
  },
  quickAdd: {
    title: 'E-Mail für externe Domain hinzufügen',
    button: 'Externe Domain hinzufügen',
    placeholder: 'z.B. example.com',
    addToCart: 'In den Warenkorb',
    addedToCart: 'Zum Warenkorb hinzugefügt',
    productAddedToCart: 'E-Mail Hosting für {domain} hinzugefügt',
    goToCart: 'Zum Warenkorb',
    error: {
      invalid: 'Bitte gib eine gültige Domain ein',
      taken: 'Diese Domain ist bereits im Warenkorb'
    }
  }
} as const

export const shop = {
  title: 'Shop',
  subtitle: 'Produkte und Services für dein Business',
  viewProducts: 'Produkte ansehen',
  whyBuyHere: 'Warum bei uns kaufen?',

  categories: {
    webhosting: {
      title: 'Webhosting',
      description: 'Zuverlässiges Hosting mit SSD-Speicher, SSL-Zertifikaten und E-Mail-Postfächern.',
      features: {
        sftpCron: 'SFTP & Cron-Jobs',
        freeSsl: 'Kostenlose SSL',
        emailIncluded: 'E-Mail inklusive'
      }
    },
    kubernetes: {
      title: 'Kubernetes',
      description: 'Skalierbare Container-Orchestrierung mit unserer Managed Kubernetes Plattform.',
      features: {
        autoScaling: 'Automatisches Scaling',
        loadBalancing: 'Load Balancing',
        cicdIntegration: 'CI/CD Integration'
      }
    },
    vserver: {
      title: 'VServer (VPS)',
      description: 'Volle Root-Rechte auf dediziertem NVMe-Storage, inklusive Snapshots.',
      features: {
        fullRoot: 'Voller Root-Zugriff',
        snapshots: 'Snapshots inklusive',
        ipv6Slash112: 'IPv6 /112 pro VM'
      }
    },
    domains: {
      title: 'Domains',
      description: 'Sichere dir deine Wunschdomain aus über 500 verfügbaren Endungen.',
      features: {
        tlds500: 'Über 500 TLDs',
        dnsManagement: 'DNS Management',
        whoisProtection: 'WHOIS Schutz optional',
        dynDns: 'DynDNS inklusive'
      }
    },
    email: {
      title: 'E-Mail Hosting',
      description: 'Professionelle E-Mail auf deiner eigenen Domain mit bis zu 1.000 Postfächern.',
      features: {
        mailboxes: 'Bis zu 1.000 Postfächer',
        storage: '10 GB geteilter Speicher pro Domain',
        autoSecurity: 'DKIM/SPF/DMARC automatisch'
      }
    }
  },

  benefits: {
    instantActivation: {
      title: 'Automatisch aktiviert',
      description: 'Domains, E-Mail, VServer und Kubernetes gehen nach der Zahlung automatisch live. Webhosting schalten wir von Hand frei.'
    },
    securePayment: {
      title: 'Sicher bezahlen',
      description: 'Verschlüsselte Zahlungsabwicklung'
    },
    flexibleCancellation: {
      title: 'Flexibel kündbar',
      description: 'Monatlich kündbar, keine versteckten Kosten'
    },
    supportIncluded: {
      title: 'Support inklusive',
      description: 'Persönlicher deutschsprachiger Support'
    }
  },

  kubernetes: {
    title: 'Kubernetes',
    subtitle: 'Managed Kubernetes, das mitwächst',
    loadingProducts: 'Produkte werden geladen...',

    tabs: {
      namespaces: 'Namespaces',
      dedicatedCluster: 'Dedicated Cluster'
    },

    billingInterval: 'Abrechnungszeitraum',
    intervals: {
      monthly: 'Monatlich',
      annually: 'Jährlich'
    },
    perMonth: '/ Monat',

    popular: 'Beliebt',

    namespaceFeatures: {
      isolatedNamespace: 'Isolierter Namespace',
      dailyBackups: 'Tägliche Backups',
      monitoring247: '24/7 Monitoring',
      sslIncluded: 'SSL/TLS inklusive',
      egressIncluded: '{amount} GiB Egress inklusive'
    },

    clusterFeatures: {
      dedicatedResources: 'Dedizierte Ressourcen',
      fullIsolation: 'Vollständige Isolation',
      managedControlPlane: 'Managed Control Plane',
      prioritySupport: 'Priority Support'
    },

    addToCart: 'In den Warenkorb',
    contactUs: 'Kontaktiere uns',
    addedToCart: 'Zum Warenkorb hinzugefügt',
    productAddedToCart: '{product} wurde dem Warenkorb hinzugefügt',

    payg: {
      badge: 'Pay-as-you-go',
      subtitle: 'Nur zahlen, was du nutzt',
      baseFee: 'Grundgebühr',
      billingNote: 'Abrechnung nach reservierter oder tatsächlicher CPU & RAM (je nachdem, was höher ist). Speicher nach Kapazität, Egress nach Traffic.',
      features: {
        noMinimum: 'Kein Mindestverbrauch',
        spendingCap: 'Budget-Benachrichtigungen'
      },
      rates: {
        cpuUnit: '/Core-Std.',
        memoryUnit: '/GiB-Std.',
        storageUnit: '/GiB-Mon.',
        egressUnit: '/GiB'
      }
    },

    whyManaged: 'Warum Managed Kubernetes?',
    features: {
      quickStart: {
        title: 'Schneller Start',
        description: 'Dein Namespace ist in Minuten bereit.'
      },
      autoScaling: {
        title: 'Auto-Scaling',
        description: 'Kubernetes verwaltet Lastspitzen automatisch.'
      },
      security: {
        title: 'Sicherheit & Backups',
        description: 'Verschlüsselung & tägliche Backups.'
      },
      managed: {
        title: 'Managed Service',
        description: 'Upgrades, Patches und 24/7 Monitoring.'
      }
    }
  },

  webhosting: {
    title: 'Webhosting',
    subtitle: 'Zuverlässiges Hosting für deine Projekte',
    loadingProducts: 'Produkte werden geladen...',

    billingInterval: 'Abrechnungszeitraum',
    intervals: {
      monthly: 'Monatlich',
      quarterly: 'Vierteljährlich',
      halfAnnually: 'Halbjährlich',
      annually: 'Jährlich'
    },

    features: {
      storage: 'Speicher',
      freeSsl: 'Kostenlose SSL-Zertifikate',
      emailIncluded: 'E-Mail-Postfächer inklusive',
      backups: 'Backups auf Knopfdruck',
      support: 'Support'
    },

    addToCart: 'In den Warenkorb',
    addedToCart: 'Zum Warenkorb hinzugefügt',
    productAddedToCart: '{product} wurde dem Warenkorb hinzugefügt',

    includedTitle: 'In allen Paketen enthalten',
    included: {
      sftpCron: 'SFTP & Cron-Jobs',
      uptime: '99% Uptime SLA',
      phpSupport: 'PHP 8.x Support',
      ddosProtection: 'DDoS-Schutz',
      unlimitedTraffic: 'Unbegrenzter Traffic',
      monthlyCancellation: 'Monatlich kündbar'
    }
  },

  email: {
    title: 'E-Mail Hosting',
    subtitle: 'Professionelle E-Mail auf deiner eigenen Domain',
    loadingProduct: 'Produkt wird geladen...',
    domainInput: {
      label: 'Domain',
      placeholder: 'z.B. example.com',
      hint: 'Die Domain, für die du E-Mail Hosting einrichten möchtest',
      suggestions: 'Deine Domains:',
      error: {
        invalid: 'Bitte gib eine gültige Domain ein',
        taken: 'Diese Domain ist bereits im Warenkorb'
      }
    },
    addToCart: 'In den Warenkorb',
    addedToCart: 'Zum Warenkorb hinzugefügt',
    productAddedToCart: 'E-Mail Hosting für {domain} hinzugefügt',
    goToCart: 'Zum Warenkorb',
    perMonth: '/ Monat',
    features: {
      mailboxes: 'Bis zu 1.000 Postfächer & 5.000 Aliasse',
      storage: '{storage} GB geteilter Speicher pro Domain',
      autoSecurity: 'DKIM/SPF/DMARC automatisch',
      outbound: '{count} E-Mails/Monat inklusive',
      cancelMonthly: 'Monatlich kündbar'
    }
  }
} as const

export const packet = {
  title: 'Pakete',
  subtitle: 'Verwalte deine Hosting-Pakete und Abonnements',
  loading: 'Pakete werden geladen...',
  restoreError: 'Kündigung konnte nicht zurückgenommen werden',
  cancelError: 'Kündigung konnte nicht durchgeführt werden',
  stripeUnavailable: 'Die Zahlungsabwicklung war gerade nicht erreichbar, deshalb wurde nichts geändert. Bitte versuche es in ein paar Minuten erneut.',
  invoiceOpen: 'Bezahle zuerst die offene Rechnung, dann kannst du die Kündigung widerrufen.',
  stats: {
    total: 'Gesamt',
    active: 'Aktiv',
    canceled: 'Gekündigt'
  },
  empty: {
    title: 'Keine Pakete vorhanden',
    description: 'Du hast noch keine Hosting-Pakete. Besuche unseren Shop um ein Paket zu erwerben.'
  },
  withdrawPage: {
    title: 'Vertrag widerrufen',
    subtitle: 'Verträge, deren Widerrufsfrist noch läuft',
    intro: 'Hier siehst du alle Verträge deines Kontos, deren Widerrufsfrist noch läuft. Sobald dein Widerruf bei uns eingegangen ist, bekommst du per E-Mail eine Eingangsbestätigung.',
    instructionLink: 'Zur Widerrufsbelehrung',
    loading: 'Verträge werden geladen...',
    empty: {
      title: 'Kein Vertrag in der Widerrufsfrist',
      description: 'Derzeit läuft für keinen deiner Verträge eine Widerrufsfrist. Buchst du ein Paket, findest du es hier, solange seine Widerrufsfrist läuft.'
    }
  },
  domains: {
    title: 'Domain-Verträge',
    hint: 'Domain-Verträge kündigst du direkt bei der jeweiligen Domain unter "Verwalten".',
    manage: 'Verwalten'
  },
  card: {
    status: {
      active: 'Aktiv',
      canceled: 'Gekündigt'
    },
    storage: {
      label: 'Speicherplatz',
      used: 'belegt'
    },
    details: {
      canceledUntil: 'Gekündigt zum',
      activeUntil: 'Laufzeit bis',
      cancelPeriod: 'Kündigungsfrist',
      cancelPeriodMonthly: 'Zum Ende des Abrechnungsmonats',
      cancelPeriodPrepaid: '1 Monat zum Monatsstichtag',
      paymentMethod: 'Zahlungsweise',
      refund: 'Erstattung',
      refundPending: '{amount} nach dem Vertragsende',
      refunded: '{amount} am {date} erstattet'
    },
    actions: {
      manageEmail: 'E-Mail verwalten',
      restore: 'Wiederherstellen',
      cancel: 'Kündigen'
    },
    modal: {
      cancel: {
        title: 'Paket kündigen',
        message: 'Möchtest du dein Paket {name} wirklich zum {date} kündigen?',
        mailWarning: 'E-Mail auf den Domains, die mit diesem Paket verknüpft sind, läuft bis zum {date} weiter. Danach werden Postfächer, Aliase und Listen dort abgeschaltet, sofern du für die jeweilige Domain kein E-Mail-Hosting buchst. Die Daten bleiben gespeichert.',
        overdueNote: 'Ist eine Zahlung für dieses Paket überfällig oder wird sie vor diesem Datum überfällig, endet das Paket stattdessen sofort und mit ihm die E-Mail auf seinen Domains. Bezahlst du die offene Rechnung, läuft es wieder bis zum Ende des Zeitraums, den sie abdeckt.',
        overdueNotePrepaid: 'Ist eine Zahlung für dieses Paket überfällig oder wird sie vor diesem Datum überfällig, endet das Paket stattdessen sofort und mit ihm die E-Mail auf seinen Domains. Bezahlst du die offene Rechnung, läuft es wieder bis zum Vertragsende, und danach erstatten wir dir die ungenutzten vollen Monate.',
        refund: 'Dein Paket ist bis zum {paidUntil} bezahlt. Nach dem Vertragsende erstatten wir dir die ungenutzten vollen Monate, {amount}, auf dein ursprüngliches Zahlungsmittel.',
        refundNone: 'Dein Paket ist bis zum {paidUntil} bezahlt. Nach dem Vertragsende bleibt davon kein voller Monat übrig, deshalb gibt es keine Erstattung.',
        refundUnknown: 'Dein Paket ist bis zum {paidUntil} bezahlt. Die ungenutzten vollen Monate erstatten wir dir nach dem Vertragsende.',
        refundProrated: 'Dein Paket ist bis zum {paidUntil} bezahlt. Die Zeit danach bis zum Vertragsende am {date} rechnen wir anteilig ab, eine Erstattung gibt es nicht.',
        refundOverdue: 'Dein Paket war bis zum {paidUntil} bezahlt, die Rechnung für den Zeitraum danach ist offen. Bezahlst du sie, läuft dein Paket bis zum Vertragsende am {date}, und wir erstatten dir ungenutzte volle Monate auf dein ursprüngliches Zahlungsmittel.',
        confirm: 'Kündigen',
        abort: 'Abbrechen'
      },
      restore: {
        title: 'Kündigung widerrufen',
        message: 'Möchtest du deine Kündigung vom {date} zurücknehmen?',
        confirm: 'Kündigung widerrufen',
        abort: 'Abbrechen'
      },
      withdraw: {
        title: 'Widerruf',
        message: 'Du bist dabei, deinen Vertrag über {name} zu widerrufen. Damit endet das Paket.',
        mailWarning: 'E-Mail auf den Domains, die mit diesem Paket verknüpft sind, wird mit dem Widerruf innerhalb einer Stunde abgeschaltet, sofern du für die jeweilige Domain kein E-Mail-Hosting buchst. Die Daten bleiben gespeichert.',
        refundInfo: 'Erstattungsbetrag: {amount}',
        refundAll: 'Wir erstatten dir jede Zahlung für diesen Vertrag.',
        refundTimeline: 'Die Erstattung erfolgt innerhalb von 14 Tagen ab Eingang deines Widerrufs auf dein ursprüngliches Zahlungsmittel.',
        confirm: 'Widerruf bestätigen',
        abort: 'Abbrechen'
      }
    },
    withdraw: {
      button: 'Vertrag widerrufen',
      daysLeft: '{days} Tage',
      label: 'Widerrufsrecht',
      status: 'Widerrufen',
      statusReceived: 'Widerruf eingegangen',
      receivedLabel: 'Widerruf eingegangen am',
      receivedNote: 'Wir beenden den Vertrag und erstatten dir, was du dafür gezahlt hast, innerhalb von 14 Tagen ab Eingang deines Widerrufs.',
      success: {
        title: 'Widerruf eingegangen',
        message: 'Dein Paket wurde beendet. Erstattung: {amount}. Die Eingangsbestätigung bekommst du per E-Mail.',
        messageNoAmount: 'Dein Paket wurde beendet. Die Eingangsbestätigung bekommst du per E-Mail.',
        received: 'Wir beenden den Vertrag und erstatten dir, was du dafür gezahlt hast, innerhalb von 14 Tagen. Die Eingangsbestätigung bekommst du per E-Mail.'
      },
      error: {
        expired: 'Die Widerrufsfrist ist abgelaufen',
        alreadyWithdrawn: 'Für diesen Vertrag ist bereits ein Widerruf eingegangen',
        notSent: 'Dein Widerruf konnte nicht übermittelt werden. Bitte sende ihn noch einmal ab.'
      }
    }
  },
} as const

export const kubernetes = {
  title: 'Kubernetes',
  subtitle: 'Nutzung und Abrechnung deines Kubernetes-Namespace',
  billingPeriod: 'Abrechnungszeitraum',
  currentUsage: 'Aktuelle Nutzung',
  projectedBill: 'Voraussichtliche Rechnung',
  projectedDisclaimer: 'Unverbindliche Schätzung inkl. 19 % USt. auf Basis der bisherigen Nutzung im laufenden Monat. Der tatsächliche Rechnungsbetrag kann abweichen.',
  projectedDisclaimerNet: 'Unverbindliche Schätzung, netto ohne USt., auf Basis der bisherigen Nutzung im laufenden Monat. Der tatsächliche Rechnungsbetrag kann abweichen.',
  paygEstimateTitle: 'Was würde Pay-as-you-go kosten?',
  paygEstimateIntro: 'Geschätzt aus der tatsächlich gemessenen Nutzung dieses Namespace im laufenden Zeitraum – damit du siehst, was ein Wechsel zu Pay-as-you-go kosten würde.',
  paygEstimateDisclaimer: 'Unverbindliche Schätzung inkl. 19 % USt. Für die Nutzung in deinem aktuellen Tarif wird dir nichts berechnet; dies ist nur, was Pay-as-you-go kosten würde.',
  paygEstimateDisclaimerNet: 'Unverbindliche Schätzung, netto ohne USt. Für die Nutzung in deinem aktuellen Tarif wird dir nichts berechnet; dies ist nur, was Pay-as-you-go kosten würde.',
  paygEstimateCta: 'Auf Pay-as-you-go wechseln',
  baseFee: 'Grundgebühr',
  minimumAsleep: 'Minimum (alle Workloads schlafen)',
  total: 'Gesamt',
  totalGross: 'Gesamt',
  lowConfidence: 'Hochgerechnet aus weniger als einem Tag Daten',
  hours: 'Stunden',
  days: 'Tage',
  noUsageData: 'Noch keine Nutzungsdaten verfügbar',
  unavailable: 'Kubernetes-Abrechnung ist für dieses Konto nicht eingerichtet.',
  noSubscription: {
    title: 'Kein Kubernetes-Paket',
    description: 'Du hast noch kein Kubernetes-Paket. Starte jetzt mit einem Kubernetes-Namespace.',
    action: 'Kubernetes-Pakete ansehen'
  },
  spendingCap: {
    title: 'Budget-Benachrichtigungen',
    label: 'Budget (EUR)',
    notSet: 'Kein Budget gesetzt. Klicke auf Bearbeiten um E-Mail-Benachrichtigungen bei Budgetüberschreitung einzurichten.',
    hint: 'Du erhältst E-Mail-Benachrichtigungen bei 75%, 90% und 100%. Dies ist kein hartes Limit – deine Workloads laufen weiter. Leer lassen zum Entfernen.'
  },
  notifications: {
    capSaved: 'Ausgabenlimit gespeichert',
    kubeconfigDownloaded: 'Kubeconfig heruntergeladen',
    gitConfigured: 'Git-Repository konfiguriert',
    gitRemoved: 'Git-Konfiguration entfernt',
    domainAdded: 'Domain hinzugefügt',
    domainDeleted: 'Domain gelöscht',
    domainVerified: 'Domain verifiziert'
  },
  config: {
    title: 'Konfiguration',
    namespace: 'Namespace',
    status: 'Status',
    provisionedAt: 'Bereitgestellt am',
    errorMessage: 'Fehler',
    downloadKubeconfig: 'Kubeconfig herunterladen',
    docsLink: 'Dokumentation',
    statuses: {
      provisioning: 'Wird bereitgestellt',
      active: 'Aktiv',
      failed: 'Fehlgeschlagen',
      deprovisioning: 'Wird entfernt',
      deprovisioned: 'Entfernt',
      suspended: 'Pausiert'
    }
  },
  free: {
    badge: 'Free',
    title: 'Free Namespace',
    description:
      'Dauerhaft kostenloser Kubernetes-Namespace mit voller K8s-API.',
    limits: {
      cpu: '200m vCPU',
      memory: '512 MiB RAM',
      pods: '5 Pods',
      storage: '1 GiB NFS-Storage',
      egress: '10 GiB ausgehender Traffic pro Monat',
      parentDomain: 'Eigene Subdomains unter *.itsh-apps.dev (keine Custom Domains)'
    },
    claimCta: 'Free Namespace anfordern',
    claimSuccess: 'Dein Free Namespace ist bereit',
    claimErrors: {
      phoneRequired: 'Bitte verifiziere deine Telefonnummer.',
      alreadyHasFree: 'Du hast bereits einen Free-Namespace beansprucht.',
      phoneAlreadyClaimed:
        'Diese Telefonnummer ist bereits mit einem anderen aktiven Konto verknüpft.',
      vatDuplicate:
        'Diese USt-ID ist bereits mit einem anderen Free-Konto verknüpft.',
      disposableEmail:
        'Wegwerf-E-Mail-Adressen sind für den Free-Tier nicht zulässig.',
      subnetRateLimited:
        'Aus deinem Netzwerk wurden in den letzten Tagen zu viele Free-Anforderungen gestellt. Bitte versuche es später erneut oder kontaktiere den Support.',
      datacenterNotAllowed:
        'Der Free Tier ist von Cloud- und Hosting-Netzwerken (VPN, Server, Rechenzentrum) aus nicht verfügbar. Bitte fordere ihn über eine private Internetverbindung an. Unsere kostenpflichtigen Tarife sind uneingeschränkt nutzbar.',
      turnstileFailed: 'Bot-Schutz-Prüfung fehlgeschlagen, bitte erneut versuchen.',
      sanctionedJurisdiction:
        'Der Free Tier kann aus rechtlichen Gründen in deiner Region nicht bereitgestellt werden.',
      outOfRegion:
        'Der Free Tier ist derzeit nur in EU/EEA, Großbritannien, USA, Kanada, Australien, Neuseeland und der Schweiz verfügbar. Unsere kostenpflichtigen Kubernetes-Tarife sind weltweit verfügbar.',
      emailNotVerified:
        'Bitte bestätige zuerst deine E-Mail-Adresse. Wir haben dir einen Link geschickt — sieh in deinem Posteingang nach.',
      notAllowed:
        'Dein Konto kann den Free Tier derzeit nicht anfordern. Bitte wende dich an den Support.'
    },
    refusal: {
      paidPlansCta: 'Kubernetes-Tarife ansehen'
    },
    cardGate: {
      message:
        'Zur Bestätigung deiner Identität benötigen wir eine hinterlegte Zahlungsmethode. Deine Karte wird nicht belastet — es wird lediglich eine 0-Euro-Verifizierung durchgeführt.',
      cta: 'Zahlungsmethode hinterlegen',
      setupFailed:
        'Die Zahlungsmethode konnte nicht eingerichtet werden. Bitte versuche es erneut.'
    },
    egressBar: {
      label: 'Ausgehender Traffic',
      capLabel: 'von {cap} GiB diesen Monat',
      warningAt80: 'Du hast 80 % deines monatlichen Traffic-Kontingents verbraucht.',
      reachedCap:
        'Monats-Traffic-Limit erreicht. Der Namespace wurde pausiert und wird am 1. nächsten Monats automatisch fortgesetzt.'
    },
    inactivityCountdown: {
      warningTitle: 'Inaktivität erkannt',
      warningBody:
        'Wir haben in den letzten {days} Tagen keinen Traffic gesehen. Bei 30 Tagen Inaktivität wird der Namespace pausiert.',
      suspendedTitle: 'Wegen Inaktivität pausiert',
      suspendedBody:
        '30 Tage ohne Traffic. Klicke auf „Fortsetzen", um deinen Namespace wieder zu aktivieren.'
    },
    suspendBanner: {
      titleEgressCap: 'Free-Tier-Traffic-Limit erreicht',
      titleInactivity: 'Wegen Inaktivität pausiert',
      titleAbuse: 'Wegen einer Missbrauchsmeldung pausiert',
      titleGeneric: 'Namespace pausiert',
      resumeButton: 'Fortsetzen',
      upgradeButton: 'Auf bezahlten Tarif upgraden',
      autoReset: 'Wird am 1. {month} automatisch fortgesetzt.',
      contactSupport: 'Bitte wende dich an den Support.'
    },
    customDomain: {
      blocked: 'Custom Domains',
      blockedTooltip:
        'Custom Domains sind im Free-Tier nicht verfügbar. Upgrade auf einen bezahlten Tarif, um eigene Domains zu nutzen.',
      upgradeCta: 'Upgrade durchführen'
    },
    upgrade: {
      title: 'Free → Bezahlt upgraden',
      description:
        'Deine Workloads laufen ohne Unterbrechung weiter — gleicher Namespace, gleiches Kubeconfig.',
      paygTitle: 'Pay-as-you-go',
      paygDescription:
        'Nutzungsbasierte Abrechnung. Volle Quotas, Custom Domains, kein Egress-Limit.',
      cartFallback:
        'Festpreis-Tarife (S/M/L) sind über den Warenkorb verfügbar.',
      confirm: 'Jetzt upgraden',
      success: 'Upgrade abgeschlossen – willkommen im bezahlten Tarif.'
    },
    release: {
      confirmTitle: 'Free Namespace freigeben',
      confirmBody:
        'Dies löscht deinen Namespace und alle darin laufenden Workloads. Aufgrund der Free-Tier-Regeln (eine kostenlose Anforderung pro Konto) kannst du danach keinen neuen Free Namespace anfordern.',
      confirmAction: 'Endgültig freigeben',
      success: 'Free Namespace freigegeben.'
    }
  },
  git: {
    title: 'GitOps (ArgoCD)',
    repoUrl: 'Repository-URL',
    path: 'Pfad',
    revision: 'Revision',
    selfHeal: 'Self-Heal',
    syncStatus: 'Sync-Status',
    healthStatus: 'Health-Status',
    notConfigured: 'Kein Git-Repository konfiguriert.',
    configure: 'Konfigurieren',
    remove: 'Entfernen',
    confirmRemove: 'Möchtest du die GitOps-Konfiguration wirklich entfernen?'
  },
  domains: {
    title: 'Eigene Domains',
    addDomain: 'Domain hinzufügen',
    domain: 'Domain',
    status: 'Status',
    verified: 'Verifiziert',
    pending: 'Ausstehend',
    verificationInstructions: 'Füge folgenden TXT-Record zu deinem DNS hinzu:',
    txtRecord: 'TXT-Record',
    host: 'Host',
    value: 'Wert',
    verify: 'Verifizieren',
    noDomains: 'Keine eigenen Domains konfiguriert.',
    autoDns: 'Auto-DNS',
    autoDnsHint: 'DNS-Einträge automatisch auf den Cluster setzen',
    autoDnsEnabled: 'Auto-DNS aktiviert',
    autoDnsDisabled: 'Auto-DNS deaktiviert'
  },
  overview: {
    title: 'Übersicht',
    quotaUsage: 'Ressourcen-Kontingente',
    workloads: 'Workloads',
    used: 'Genutzt',
    limit: 'Limit'
  },
  tabs: {
    overview: 'Übersicht',
    workloads: 'Workloads',
    pods: 'Pods',
    events: 'Ereignisse',
    storage: 'Speicher',
    usage: 'Nutzung',
    config: 'Konfiguration'
  },
  workloads: {
    title: 'Workloads',
    name: 'Name',
    kind: 'Art',
    replicas: 'Replicas',
    age: 'Alter',
    noWorkloads: 'Keine Workloads deployed.',
    health: 'Health'
  },
  pods: {
    title: 'Pods',
    name: 'Name',
    status: 'Status',
    restarts: 'Neustarts',
    age: 'Alter',
    containers: 'Container',
    noPods: 'Keine Pods aktiv.'
  },
  events: {
    title: 'Ereignisse',
    type: 'Typ',
    reason: 'Grund',
    message: 'Nachricht',
    object: 'Objekt',
    time: 'Zeit',
    count: 'Anzahl',
    noEvents: 'Keine aktuellen Ereignisse.',
    all: 'Alle',
    warning: 'Warnung',
    normal: 'Normal'
  },
  storage: {
    title: 'Persistente Volumes',
    name: 'Name',
    capacity: 'Kapazität',
    used: 'Belegt',
    status: 'Status',
    storageClass: 'Speicherklasse',
    mountedBy: 'Eingehängt von',
    age: 'Alter',
    noStorage: 'Keine persistenten Volumes konfiguriert.',
    autoscaling: 'Auto-Erweiterung',
    autoscalingLimit: 'Limit',
    billedAs: 'Abgerechnet als {size}',
    billableHint: 'Mindestgröße für Block Storage',
    statuses: {
      bound: 'Gebunden',
      pending: 'Ausstehend',
      lost: 'Verloren'
    }
  },
  logs: {
    container: 'Container',
    lines: 'Zeilen',
    autoRefresh: 'Auto-Aktualisierung',
    noLogs: 'Keine Logs verfügbar.',
    notStarted: 'Dieser Container startet noch und hat deshalb noch keine Logs.',
    requestRejected: 'Logs konnten für diesen Container nicht abgerufen werden.',
    podGone: 'Dieser Pod existiert nicht mehr.'
  },
  costByWorkload: 'Kosten pro Workload',
  costByWorkloadEstimate: 'Geschätzte Kosten pro Workload (Pay-as-you-go)',
  usageHistory: 'Nutzungsverlauf',
  sleep: {
    title: 'Scale-to-Zero',
    intro: 'Diese Workloads fahren auf null Replicas herunter, wenn keine Anfragen kommen. Solange sie schlafen, berechnen wir dir keine CPU und keinen Arbeitsspeicher. Speicher und Grundgebühr laufen weiter.',
    state: 'Status',
    asleep: 'Schläft',
    awake: 'Wach',
    whenAwake: 'Kosten wenn wach',
    perHour: '≈ {amount}/Std.'
  },
  efficiency: {
    title: 'Ressourcen-Effizienz',
    cpu: 'CPU',
    memory: 'RAM',
    savings: 'Einsparpotenzial',
    totalSavings: 'Gesamtes Einsparpotenzial',
    noData: 'Keine Effizienzdaten verfügbar.',
    peak7d: 'Spitze 7T',
    cpuOk: 'Gute Auslastung — Spitze 7T: {used} von {requested}.',
    cpuIdle: 'Keine CPU-Nutzung (7T). Request {requested} — ggf. reduzieren.',
    cpuReduce: 'Spitze 7T: {used} von {requested}. Reduzierung auf ~{suggested} spart Kosten.',
    cpuIncrease: 'Spitze 7T: {used}, nur {requested} angefordert (Limit: {limit}). Request auf ~{suggested} erhöhen.',
    cpuIncreaseNoLimit: 'Spitze 7T: {used}, nur {requested} angefordert. Request auf ~{suggested} erhöhen.',
    memOk: 'Gute Auslastung — Spitze 7T: {used} von {requested}.',
    memIdle: 'Keine RAM-Nutzung (7T). Request {requested} — ggf. reduzieren.',
    memReduce: 'Spitze 7T: {used} von {requested}. Reduzierung auf ~{suggested} spart Kosten.',
    memIncrease: 'Spitze 7T: {used}, nur {requested} angefordert (Limit: {limit}). Request auf ~{suggested} erhöhen.',
    memIncreaseNoLimit: 'Spitze 7T: {used}, nur {requested} angefordert. Request auf ~{suggested} erhöhen.'
  },
  planManagement: {
    title: 'Planverwaltung',
    currentPlan: 'Aktuell: {name}, {price}',
    downgrade: 'Downgrade',
    upgrade: 'Upgrade',
    current: 'Aktuell',
    payg: {
      badge: '{percent}% sparen',
      description: 'Basierend auf deiner Nutzung: ~{price}/Mo.',
      cta: 'Zu Pay-as-you-go wechseln'
    },
    dialog: {
      title: 'Plan wechseln',
      tierTitle: 'Zu {name} wechseln',
      paygTitle: 'Zu Pay-as-you-go wechseln',
      from: 'Aktuell',
      to: 'Neuer Plan',
      price: 'Preis',
      quotas: 'Ressourcen-Kontingente',
      cpu: 'CPU',
      memory: 'Arbeitsspeicher',
      proration: 'Dein aktueller Plan wird anteilig abgerechnet. Der neue Plan beginnt sofort.',
      paygExplain: 'Pay-as-you-go rechnet nach tatsächlicher Ressourcennutzung ab. Keine Bindung, jederzeit kündbar.',
      quotaUpgrade: 'Kontingente steigen auf {cpu} vCPU / {memory} GiB RAM.',
      confirm: 'Wechsel bestätigen',
      cancel: 'Abbrechen',
      success: 'Plan erfolgreich gewechselt',
      error: 'Plan konnte nicht gewechselt werden'
    }
  }
} as const
